import {SelectProgramTemplate} from "@client/components/ProgramCard";
import * as forms from "@client/forms";
import {Label} from "@client/forms";
import * as analytics from "@client/shared/analytics";
import {Register} from "@client/site/components";
import {useSiteContext, useStaticURL} from "@client/site/context";
import {Layout} from "@client/site/Layout";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js/pure";
import React from "react";

import {CARD_ELEMENT_OPTIONS} from "./ManagerRegistration";

const styles = {
    cardElement:
        "h-[35px] px-2.5 py-[5px] block text-sm shrink basis-full bg-white rounded-md font-light outline-none appearance-none border border-[#D8D8D8] flex flex-col justify-center",
};

export const HireRegistration = (props: server.manager.templates.HireRegistration) => {
    // Stripe mints the token on submit, after the form is ready, so it is not
    // a field the form waits on.
    const {token: _token, ...fields} = props.registration_form.fields;
    const registration_form = forms.useForm({...props.registration_form, fields});
    const staticURL = utils.getStaticBucketURL();
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [distinctId, setDistinctId] = React.useState("");
    const [token, setToken] = React.useState(
        forms.boundText(
            props.registration_form.drafts?.token,
            props.registration_form.defaults.token,
        ) ?? "",
    );
    const {request} = useSiteContext();
    const params = new URL(request.url).searchParams;
    const defaultProgram = props.programs.find(
        (p) => p.product_code == params.get("program"),
    );

    const [stripeError, setStripeError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setDistinctId(analytics.getDistinctId());
    }, []);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        if (!forms.ready(registration_form)) {
            return;
        }
        event.persist();
        const target = event.target as HTMLFormElement;

        if (token === "") {
            const card = elements?.getElement(CardElement);
            if (stripe == null || card == null) {
                setStripeError("Stripe could not load correctly");
                return;
            }

            const {error, paymentMethod} = await stripe.createPaymentMethod({
                type: "card",
                card,
            });
            if (paymentMethod != null) {
                setToken(paymentMethod.id);
                setIsSubmitting(true);
                // Needs to be in the next tick.
                setTimeout(() => target.submit(), 1);
            } else {
                setStripeError(error.message ?? null);
            }
        } else {
            setIsSubmitting(true);
            target.submit();
        }
    };

    const boundProgram = props.registration_form.drafts?.selected_program;

    const [selectedProgram, setSelectedProgram] = React.useState<UUID | null>(() => {
        // A bound POST already states the choice, including the choice of none,
        // so ?program= must not reach back in and undo a deliberate clear.
        if (typeof boundProgram === "string") {
            return boundProgram === "" ? null : (boundProgram as UUID);
        }
        const initial = props.registration_form.defaults.selected_program;
        return initial != null && initial !== ""
            ? (initial as UUID)
            : (defaultProgram?.uuid ?? null);
    });

    return (
        <Layout narrow title={`Register with ${props.manager_name}`}>
            <form
                noValidate
                method="POST"
                action=""
                className="space-y-4"
                onSubmit={handleSubmit}
                style={{textWrap: "pretty"}}
            >
                <forms.CSRFToken />
                <forms.FormErrors form={registration_form} />
                <div className="flex items-center gap-4">
                    <img
                        className="inline-block size-16 rounded-full"
                        src={`${staticURL}pros/${props.handle}.jpg`}
                        alt=""
                    />
                    <h1 className="text-3xl">
                        Register with{" "}
                        <span className="text-orange-500">{props.manager_name}</span>
                    </h1>
                </div>
                <div className="space-y-4 sm:ml-20 sm:pl-1">
                    {props.description != null ? (
                        <div className="text-base whitespace-pre-line">
                            {props.description}
                        </div>
                    ) : (
                        <>
                            <p className="text-lg">
                                {props.manager_name} has partnered with Joy Health
                                Tracker to track your nutrition and workouts.
                            </p>

                            <p className="text-lg">
                                When you register, {props.manager_name} will have full
                                access to your nutrition and workouts.
                            </p>

                            <p className="text-lg">
                                To get started, please fill out the details below.
                            </p>
                        </>
                    )}

                    <div className="max-w-[400px] space-y-4">
                        <forms.Fields
                            form={registration_form}
                            fields={["email", "full_name", "name", "invite_code"]}
                        />
                    </div>

                    <div className="flex gap-2">
                        <forms.Fields
                            form={registration_form}
                            fields={["date_of_birth", "gender"]}
                        />
                    </div>

                    {registration_form.order.includes("height") &&
                        registration_form.order.includes("weight") && (
                            <div className="flex gap-4">
                                <forms.Fields
                                    form={registration_form}
                                    fields={["height", "weight"]}
                                />
                            </div>
                        )}

                    <input type="hidden" name="distinct_id" defaultValue={distinctId} />
                    <input type="hidden" name="token" defaultValue={token} />
                    <input
                        type="hidden"
                        name="selected_program"
                        defaultValue={selectedProgram ?? ""}
                    />
                    {props.programs.length > 0 && (
                        <div className="space-y-2 py-1">
                            <h3>Select your program</h3>
                            <SelectProgramTemplate
                                selected={selectedProgram}
                                setSelected={setSelectedProgram}
                                programs={props.programs}
                            />
                        </div>
                    )}

                    {token === "" && (
                        <Label label="Payment details" error={stripeError}>
                            <CardElement
                                className={styles.cardElement}
                                options={CARD_ELEMENT_OPTIONS}
                            />
                        </Label>
                    )}

                    <div className="flex items-center gap-4 text-sm">
                        <forms.Fields
                            form={registration_form}
                            noLabel
                            fields={["agreed_to_billing"]}
                        />
                        {props.stripe_account != null ? (
                            <p className="leading-relaxed">
                                I understand <strong>{props.manager_name}</strong> will
                                bill me{" "}
                                <strong>
                                    {utils.formatCurrency(
                                        props.stripe_product_price,
                                        false,
                                    )}{" "}
                                    per month
                                </strong>{" "}
                                until cancelled.
                                <br />
                                This fee is billed <strong>directly</strong> by{" "}
                                {props.manager_name}.
                                <br />
                                Joy Health Tracker is <strong>not</strong> involved in
                                billing for this service.
                            </p>
                        ) : (
                            <p className="leading-relaxed">
                                I understand <strong>Joy Health Tracker</strong> will
                                bill me <strong>$5 per month</strong> until cancelled.{" "}
                                <br /> This fee <strong>does not include</strong>{" "}
                                services by {props.manager_name}.
                                <br />
                                Billing for those will be handled by{" "}
                                {props.manager_name} separately.
                            </p>
                        )}
                    </div>
                    <Register isSubmitting={isSubmitting} />
                </div>
            </form>
        </Layout>
    );
};

export const Template = (props: server.manager.templates.HireRegistration) => {
    const staticURL = useStaticURL();
    const fonts = [
        {
            family: "InterVariable",
            src: `url('${staticURL}fonts/InterVariable.woff2')`,
            weight: "300",
        },
    ];

    const [stripe, setStripe] = React.useState<ReturnType<typeof loadStripe> | null>(
        null,
    );

    React.useEffect(() => {
        setStripe(
            loadStripe(props.STRIPE_PUBLISHABLE_KEY, {
                stripeAccount: props.stripe_account ?? undefined,
            }),
        );
    }, []);

    return (
        <Elements stripe={stripe} options={{fonts}}>
            <HireRegistration {...props} />
        </Elements>
    );
};
